import { useState, useRef, type ChangeEvent, type DragEvent } from 'react'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { AppDialog, type AppDialogAction } from './AppDialog'
import { FilePicker, type FileReject } from './FilePicker'
import { OverflowTip } from './Hover/OverflowTip'
import { useNls } from '../../nls/nlsProvider'
import { parseCsv, downloadCsvText, escapeCsvValue } from '../../utils/exportCsv'
import {
  parseCsvText,
  autoMapHeaders,
  type OpsImportEntityDef,
  type ImportOpsCsvResult,
  type OpsImportJobDoc,
  type OpsImportFileRefusalReason,
  type OpsImportErrorCode,
} from '../../types/ops'
import './bulkImportDialog.css'

export interface ImportColumnDef {
  id: string
  label: string
  type: 'string' | 'number' | 'select'
  required?: boolean
  options?: string[]
  validate?: (val: unknown) => string | null
}

export interface BulkImportServerConfig {
  entityDef: OpsImportEntityDef
  entityLabel?: string
  columnLabel: (key: string) => string
  formatRowError: (code: OpsImportErrorCode) => string
  classifyError: (err: unknown) => { message: string; reason?: OpsImportFileRefusalReason }
  onDryRun: (
    csvText: string,
    mapping: Record<number, string>,
  ) => Promise<ImportOpsCsvResult>
  onApply: (
    csvText: string,
    mapping: Record<number, string>,
    opId: string,
  ) => Promise<ImportOpsCsvResult>
  subscribeJob?: (
    opId: string,
    callback: (job: OpsImportJobDoc | null) => void,
  ) => () => void
  initialOpId?: string
}

export interface BulkImportDialogProps {
  open: boolean
  title?: string
  entityName?: string
  columns?: ImportColumnDef[]
  onClose: () => void
  onCommit?: (
    validRows: Array<Record<string, unknown>>,
    onProgress: (committed: number, total: number) => void,
  ) => Promise<void>
  server?: BulkImportServerConfig
}

interface ParsedRow {
  index: number
  raw: Record<string, string>
  mapped: Record<string, unknown>
  errors: Record<string, string>
  isValid: boolean
}

type ServerStep = 'upload' | 'map' | 'dryRun' | 'apply'

const createOpId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `op-import-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

export const BulkImportDialog = ({
  open,
  title,
  entityName = 'Items',
  columns = [],
  onClose,
  onCommit,
  server,
}: BulkImportDialogProps) => {
  const isServerMode = Boolean(server)
  const { t } = useNls()

  // Client-mode state
  const [clientStep, setClientStep] = useState<'upload' | 'preview' | 'commit'>('upload')
  const [isDragOver, setIsDragOver] = useState(false)
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [skipInvalidRows, setSkipInvalidRows] = useState(true)
  const [progress, setProgress] = useState<{ committed: number; total: number }>({ committed: 0, total: 0 })
  const [isClientDone, setIsClientDone] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Server-mode state
  const [serverStep, setServerStep] = useState<ServerStep>('upload')
  const [serverCsvText, setServerCsvText] = useState('')
  const [serverHeaders, setServerHeaders] = useState<string[]>([])
  const [serverMapping, setServerMapping] = useState<Record<number, string>>({})
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [dryRunResult, setDryRunResult] = useState<ImportOpsCsvResult | null>(null)
  const [applyResult, setApplyResult] = useState<ImportOpsCsvResult | null>(null)
  const [applyJobDoc, setApplyJobDoc] = useState<OpsImportJobDoc | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const opIdRef = useRef<string>(server?.initialOpId || createOpId())
  const lastAppliedRef = useRef<{ csvText: string; mappingKey: string } | null>(null)

  const resolvedTitle =
    title ??
    (isServerMode
      ? t('NLSOpsCsvImportTitle', {
          entity: server!.entityLabel || server!.entityDef.entity,
        })
      : 'Import from CSV')

  /* ---------------------------------------------------------------------------
   * Client-mode handlers
   * ---------------------------------------------------------------------------*/
  const handleClientFileProcess = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '')
      const rawMatrix = parseCsv(text)
      if (rawMatrix.length === 0) return

      const headerRow = rawMatrix[0].map((h) => h.toLowerCase().trim())
      const dataRows = rawMatrix.slice(1)

      const colMapping: Record<string, number> = {}
      for (const col of columns) {
        const exactIndex = headerRow.indexOf(col.id.toLowerCase())
        if (exactIndex !== -1) {
          colMapping[col.id] = exactIndex
          continue
        }
        const labelIndex = headerRow.indexOf(col.label.toLowerCase())
        if (labelIndex !== -1) {
          colMapping[col.id] = labelIndex
          continue
        }
      }

      const parsedList: ParsedRow[] = dataRows.map((rawRow, rowIndex) => {
        const raw: Record<string, string> = {}
        const mapped: Record<string, unknown> = {}
        const errors: Record<string, string> = {}

        for (const col of columns) {
          const colIdx = colMapping[col.id]
          const cellVal = colIdx !== undefined && colIdx < rawRow.length ? rawRow[colIdx] : ''
          raw[col.id] = cellVal

          if (col.required && !cellVal.trim()) {
            errors[col.id] = `${col.label} is required`
            continue
          }

          if (col.type === 'number') {
            if (cellVal.trim() === '') {
              mapped[col.id] = null
            } else {
              const num = Number(cellVal)
              if (isNaN(num)) {
                errors[col.id] = `${col.label} must be a number`
              } else {
                mapped[col.id] = num
              }
            }
          } else {
            mapped[col.id] = cellVal.trim()
          }

          if (col.validate && cellVal.trim() !== '') {
            const err = col.validate(mapped[col.id])
            if (err) errors[col.id] = err
          }
        }

        return {
          index: rowIndex + 1,
          raw,
          mapped,
          errors,
          isValid: Object.keys(errors).length === 0,
        }
      })

      setRows(parsedList)
      setClientStep('preview')
    }
    reader.readAsText(file)
  }

  const handleClientFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleClientFileProcess(file)
  }

  const handleClientDrop = (e: DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleClientFileProcess(file)
  }

  const validRows = rows.filter((r) => r.isValid)
  const invalidRows = rows.filter((r) => !r.isValid)
  const rowsToCommit = skipInvalidRows ? validRows : rows
  const canClientCommit = rowsToCommit.length > 0 && (skipInvalidRows || invalidRows.length === 0)

  const handleClientCommit = async () => {
    if (!canClientCommit || isSubmitting || !onCommit) return
    setClientStep('commit')
    setIsSubmitting(true)
    setProgress({ committed: 0, total: rowsToCommit.length })

    try {
      await onCommit(
        rowsToCommit.map((r) => r.mapped),
        (committed, total) => setProgress({ committed, total }),
      )
      setIsClientDone(true)
    } catch (err) {
      console.warn('[BulkImportDialog] Commit failed', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  /* ---------------------------------------------------------------------------
   * Server-mode handlers
   * ---------------------------------------------------------------------------*/
  const handleServerFiles = (files: File[]) => {
    const file = files[0]
    if (!file) return
    setUploadError(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '')
      try {
        const parsed = parseCsvText(text)
        if (parsed.header.length === 0) {
          setUploadError(t('NLSOpsCsvFileErrorParseError'))
          return
        }
        setServerCsvText(text)
        setServerHeaders(parsed.header)

        const auto = autoMapHeaders(parsed.header, server!.entityDef.entity)
        setServerMapping(auto.mapping)
        setServerStep('map')
      } catch (err: unknown) {
        setUploadError(server!.classifyError(err).message)
      }
    }
    reader.onerror = () => {
      setUploadError(t('NLSOpsCsvFileErrorParseError'))
    }
    reader.readAsText(file)
  }

  const handleServerReject = (rejects: FileReject[]) => {
    if (rejects.length > 0) {
      setUploadError(t('NLSOpsCsvFileErrorTooLarge'))
    }
  }

  const handleRunDryRun = async () => {
    if (!server) return
    setIsSubmitting(true)
    setActionError(null)
    try {
      const res = await server.onDryRun(serverCsvText, serverMapping)
      setDryRunResult(res)
      setServerStep('dryRun')
    } catch (err: unknown) {
      setActionError(server.classifyError(err).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRunApply = async () => {
    if (!server) return
    const currentMappingKey = JSON.stringify(serverMapping)
    if (
      lastAppliedRef.current &&
      (lastAppliedRef.current.csvText !== serverCsvText ||
        lastAppliedRef.current.mappingKey !== currentMappingKey)
    ) {
      opIdRef.current = createOpId()
    }
    lastAppliedRef.current = { csvText: serverCsvText, mappingKey: currentMappingKey }

    setIsSubmitting(true)
    setActionError(null)
    setServerStep('apply')
    setApplyJobDoc(null)

    let unsub: (() => void) | undefined
    if (server.subscribeJob) {
      unsub = server.subscribeJob(opIdRef.current, (job) => {
        if (job) {
          setApplyJobDoc(job)
        }
      })
    }

    try {
      const res = await server.onApply(serverCsvText, serverMapping, opIdRef.current)
      setApplyResult(res)
    } catch (err: unknown) {
      setActionError(server.classifyError(err).message)
    } finally {
      setIsSubmitting(false)
      if (unsub) unsub()
    }
  }

  const handleDownloadErrors = () => {
    if (!dryRunResult || dryRunResult.errors.length === 0 || !server) return
    const headers = [
      escapeCsvValue(t('NLSOpsCsvErrorsHeaderRow')),
      escapeCsvValue(t('NLSOpsCsvErrorsHeaderColumn')),
      escapeCsvValue(t('NLSOpsCsvErrorsHeaderCode')),
      escapeCsvValue(t('NLSOpsCsvErrorsHeaderError')),
    ].join(',')
    const lines = [headers]
    for (const err of dryRunResult.errors) {
      const msg = server.formatRowError(err.code)
      lines.push(
        [
          escapeCsvValue(err.rowNumber),
          escapeCsvValue(server.columnLabel(err.field)),
          escapeCsvValue(err.code),
          escapeCsvValue(msg),
        ].join(','),
      )
    }
    const filename = `${server.entityDef.entity.toLowerCase()}-import-errors.csv`
    downloadCsvText(filename, lines.join('\r\n'))
  }

  /* ---------------------------------------------------------------------------
   * Reset & Close
   * ---------------------------------------------------------------------------*/
  const resetState = () => {
    if (isServerMode) {
      setServerStep('upload')
      setServerCsvText('')
      setServerHeaders([])
      setServerMapping({})
      setUploadError(null)
      setActionError(null)
      setDryRunResult(null)
      setApplyResult(null)
      setApplyJobDoc(null)
      setIsSubmitting(false)
    } else {
      setClientStep('upload')
      setRows([])
      setIsClientDone(false)
      setProgress({ committed: 0, total: 0 })
      setIsSubmitting(false)
    }
    onClose()
  }

  /* ---------------------------------------------------------------------------
   * Server-mode Mapping Validation
   * ---------------------------------------------------------------------------*/
  const importableColumns = server
    ? server.entityDef.columns.filter((c) => !c.readOnly)
    : []

  // Check for duplicate mappings
  const destinationCounts: Record<string, number> = {}
  for (const dest of Object.values(serverMapping)) {
    if (dest) {
      destinationCounts[dest] = (destinationCounts[dest] || 0) + 1
    }
  }
  const duplicateCol = Object.keys(destinationCounts).find((k) => destinationCounts[k] > 1)

  // Check for unmapped required columns
  const mappedKeys = new Set(Object.values(serverMapping))
  const unmappedRequiredCols = importableColumns.filter(
    (c) => c.required && !mappedKeys.has(c.key),
  )

  const canAdvanceMap =
    !duplicateCol && unmappedRequiredCols.length === 0 && !isSubmitting

  /* ---------------------------------------------------------------------------
   * Dry Run Error / Warning separation
   * ---------------------------------------------------------------------------*/
  const dryRunFatalErrors =
    dryRunResult?.errors.filter((e) => e.code !== 'QUANTITY_IGNORED') ?? []
  const dryRunWarnings =
    dryRunResult?.errors.filter((e) => e.code === 'QUANTITY_IGNORED') ?? []
  const canServerApply =
    dryRunResult !== null && dryRunFatalErrors.length === 0 && !isSubmitting

  /* ---------------------------------------------------------------------------
   * Apply status derivation
   * ---------------------------------------------------------------------------*/
  const isServerDone =
    applyJobDoc?.status === 'DONE' ||
    applyResult?.status === 'DONE' ||
    (applyResult && !applyResult.errors?.length && !applyJobDoc)

  const isServerPartial =
    applyJobDoc?.status === 'PARTIAL' ||
    applyResult?.status === 'PARTIAL'

  /* ---------------------------------------------------------------------------
   * Dialog Actions
   * ---------------------------------------------------------------------------*/
  let actions: AppDialogAction[] = []

  if (!isServerMode) {
    if (clientStep === 'preview') {
      actions = [
        {
          label: t('NLSCommonActionBack'),
          variant: 'ghost',
          onClick: () => setClientStep('upload'),
        },
        {
          label: t('NLSCommonActionCancel'),
          variant: 'ghost',
          onClick: resetState,
        },
        {
          label: t('NLSCommonBulkImportActionImportCount', {
            count: rowsToCommit.length,
            entityName,
          }),
          variant: 'primary',
          testId: 'commit-import-btn',
          disabled: !canClientCommit,
          onClick: () => void handleClientCommit(),
        },
      ]
    } else if (clientStep === 'commit' && isClientDone) {
      actions = [
        {
          label: t('NLSCommonBulkImportActionDone'),
          variant: 'primary',
          onClick: resetState,
        },
      ]
    }
  } else {
    // Server mode actions
    if (serverStep === 'map') {
      actions = [
        {
          label: t('NLSCommonActionBack'),
          variant: 'ghost',
          onClick: () => setServerStep('upload'),
        },
        {
          label: t('NLSCommonActionCancel'),
          variant: 'ghost',
          onClick: resetState,
        },
        {
          label: t('NLSOpsCsvActionDryRun'),
          variant: 'primary',
          testId: 'dry-run-btn',
          disabled: !canAdvanceMap,
          onClick: () => void handleRunDryRun(),
        },
      ]
    } else if (serverStep === 'dryRun') {
      actions = [
        {
          label: t('NLSCommonActionBack'),
          variant: 'ghost',
          onClick: () => setServerStep('map'),
        },
        {
          label: t('NLSCommonActionCancel'),
          variant: 'ghost',
          onClick: resetState,
        },
        {
          label: t('NLSOpsCsvActionApply'),
          variant: 'primary',
          testId: 'apply-import-btn',
          disabled: !canServerApply,
          onClick: () => void handleRunApply(),
        },
      ]
    } else if (serverStep === 'apply') {
      if (isServerDone) {
        actions = [
          {
            label: t('NLSCommonBulkImportActionDone'),
            variant: 'primary',
            onClick: resetState,
          },
        ]
      } else if (isServerPartial || actionError) {
        actions = [
          {
            label: t('NLSCommonActionBack'),
            variant: 'ghost',
            onClick: () => setServerStep('map'),
          },
          {
            label: t('NLSCommonActionCancel'),
            variant: 'ghost',
            onClick: resetState,
          },
          {
            label: t('NLSOpsCsvActionResume'),
            variant: 'primary',
            testId: 'resume-import-btn',
            disabled: isSubmitting,
            onClick: () => void handleRunApply(),
          },
        ]
      }
    }
  }

  return (
    <AppDialog
      open={open}
      size="full"
      title={resolvedTitle}
      headerExtra={
        !isServerMode ? (
          <div className="bulkImportDialog__stepIndicator">
            <span className={`bulkImportDialog__stepDot ${clientStep === 'upload' ? 'isActive' : ''}`}>1</span>
            <span>{t('NLSCommonBulkImportStepUpload')}</span>
            <span>›</span>
            <span className={`bulkImportDialog__stepDot ${clientStep === 'preview' ? 'isActive' : ''}`}>2</span>
            <span>{t('NLSCommonBulkImportStepPreview')}</span>
            <span>›</span>
            <span className={`bulkImportDialog__stepDot ${clientStep === 'commit' ? 'isActive' : ''}`}>3</span>
            <span>{t('NLSCommonBulkImportStepCommit')}</span>
          </div>
        ) : (
          <div className="bulkImportDialog__stepIndicator">
            <span className={`bulkImportDialog__stepDot ${serverStep === 'upload' ? 'isActive' : ''}`}>1</span>
            <span>{t('NLSOpsCsvStepUpload')}</span>
            <span>›</span>
            <span className={`bulkImportDialog__stepDot ${serverStep === 'map' ? 'isActive' : ''}`}>2</span>
            <span>{t('NLSOpsCsvStepMap')}</span>
            <span>›</span>
            <span className={`bulkImportDialog__stepDot ${serverStep === 'dryRun' ? 'isActive' : ''}`}>3</span>
            <span>{t('NLSOpsCsvStepDryRun')}</span>
            <span>›</span>
            <span className={`bulkImportDialog__stepDot ${serverStep === 'apply' ? 'isActive' : ''}`}>4</span>
            <span>{t('NLSOpsCsvStepApply')}</span>
          </div>
        )
      }
      onDismiss={resetState}
      actions={actions}
    >
      <div className="bulkImportDialog" data-testid={isServerMode ? 'server-bulk-import' : 'bulk-import-dialog'}>
        <div className="bulkImportDialog__body">
          {/* ===============================================================
              CLIENT MODE BODY
              =============================================================== */}
          {!isServerMode && (
            <>
              {clientStep === 'upload' && (
                <div
                  className={`bulkImportDropZone ${isDragOver ? 'isDragOver' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsDragOver(true)
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleClientDrop}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload CSV file"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={handleClientFileInputChange}
                    data-testid="csv-file-input"
                  />
                  <div className="bulkImportDropZone__icon">
                    <CloudUploadRoundedIcon sx={{ fontSize: 28 }} />
                  </div>
                  <h3 className="bulkImportDropZone__title">Click to upload or drag & drop a CSV file</h3>
                  <p className="bulkImportDropZone__subtitle">
                    Columns: {columns.map((c) => c.label).join(', ')}
                  </p>
                </div>
              )}

              {clientStep === 'preview' && (
                <>
                  <div className="bulkImportPreviewSummary">
                    <div className="bulkImportPreviewBadges">
                      <span className="bulkImportBadge bulkImportBadge--total">
                        Total: {rows.length}
                      </span>
                      <span className="bulkImportBadge bulkImportBadge--valid">
                        Valid: {validRows.length}
                      </span>
                      {invalidRows.length > 0 && (
                        <span className="bulkImportBadge bulkImportBadge--error">
                          Errors: {invalidRows.length}
                        </span>
                      )}
                    </div>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={skipInvalidRows}
                        onChange={(e) => setSkipInvalidRows(e.target.checked)}
                        data-testid="skip-invalid-toggle"
                      />
                      <span>Skip invalid rows</span>
                    </label>
                  </div>

                  <div className="bulkImportGridWrapper">
                    <table className="bulkImportGrid">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>#</th>
                          {columns.map((col) => (
                            <th key={col.id}>{col.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.index} className={row.isValid ? '' : 'hasError'}>
                            <td style={{ color: 'var(--muted)' }}>{row.index}</td>
                            {columns.map((col) => {
                              const hasErr = Boolean(row.errors[col.id])
                              const valStr = String(row.raw[col.id] ?? '')
                              const td = (
                                <td
                                  key={col.id}
                                  className={hasErr ? 'bulkImportCell--error' : ''}
                                  title={row.errors[col.id] || undefined}
                                >
                                  {row.raw[col.id] || <span style={{ opacity: 0.4 }}>—</span>}
                                </td>
                              )
                              if (!hasErr && valStr) {
                                return (
                                  <OverflowTip key={col.id} label={valStr}>
                                    {td}
                                  </OverflowTip>
                                )
                              }
                              return td
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {clientStep === 'commit' && (
                <div className="bulkImportProgressCard">
                  {isClientDone ? (
                    <>
                      <CheckCircleRoundedIcon sx={{ fontSize: 48, color: 'var(--success)' }} />
                      <h3>Import Complete!</h3>
                      <p style={{ color: 'var(--muted)', fontSize: 14 }}>
                        Successfully imported {progress.committed} {entityName.toLowerCase()}.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3>Importing {entityName}...</h3>
                      <div className="bulkImportProgressBar">
                        <div
                          className="bulkImportProgressBarFill"
                          style={{
                            width: `${progress.total ? (progress.committed / progress.total) * 100 : 0}%`,
                          }}
                        />
                      </div>
                      <p style={{ color: 'var(--muted)', fontSize: 13 }}>
                        {progress.committed} of {progress.total} imported
                      </p>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ===============================================================
              SERVER MODE BODY
              =============================================================== */}
          {isServerMode && server && (
            <>
              {serverStep === 'upload' && (
                <div className="bulkImportServerUpload">
                  {uploadError && (
                    <div className="bulkImportBanner bulkImportBanner--error" data-testid="upload-error-banner">
                      <ErrorOutlineRoundedIcon fontSize="small" />
                      <span>{uploadError}</span>
                    </div>
                  )}
                  <FilePicker
                    accept=".csv"
                    maxBytes={1024 * 1024}
                    multiple={false}
                    disabled={isSubmitting}
                    onFiles={handleServerFiles}
                    onReject={handleServerReject}
                  />
                </div>
              )}

              {serverStep === 'map' && (
                <div className="bulkImportServerMap">
                  {duplicateCol && (
                    <div className="bulkImportBanner bulkImportBanner--error" data-testid="duplicate-mapping-banner">
                      <ErrorOutlineRoundedIcon fontSize="small" />
                      <span>
                        {t('NLSOpsCsvDuplicateMappingError', {
                          column: duplicateCol,
                        })}
                      </span>
                    </div>
                  )}
                  {unmappedRequiredCols.length > 0 && (
                    <div className="bulkImportBanner bulkImportBanner--warning" data-testid="unmapped-required-banner">
                      <WarningAmberRoundedIcon fontSize="small" />
                      <span>
                        {t('NLSOpsCsvUnmappedRequiredWarning', {
                          columns: unmappedRequiredCols.map((c) => c.key).join(', '),
                        })}
                      </span>
                    </div>
                  )}
                  {actionError && (
                    <div className="bulkImportBanner bulkImportBanner--error" data-testid="action-error-banner">
                      <ErrorOutlineRoundedIcon fontSize="small" />
                      <span>{actionError}</span>
                    </div>
                  )}

                  <div className="bulkImportGridWrapper">
                    <table className="bulkImportGrid bulkImportMapTable">
                      <thead>
                        <tr>
                          <th className="bulkImportTh--width50">#</th>
                          <th>{t('NLSOpsCsvFileHeaderLabel')}</th>
                          <th>{t('NLSOpsCsvDestinationColumnLabel')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {serverHeaders.map((header, idx) => (
                          <tr key={idx}>
                            <td className="bulkImportTd--muted">{idx + 1}</td>
                            <td>
                              <strong>{header}</strong>
                            </td>
                            <td>
                              <select
                                className="bulkImportSelect"
                                aria-label={`Map column ${header}`}
                                value={serverMapping[idx] || ''}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setServerMapping((prev) => {
                                    const next = { ...prev }
                                    if (val) {
                                      next[idx] = val
                                    } else {
                                      delete next[idx]
                                    }
                                    return next
                                  })
                                }}
                              >
                                <option value="">{t('NLSOpsCsvActionSkipColumn')}</option>
                                {importableColumns.map((col) => (
                                  <option key={col.key} value={col.key}>
                                    {server.columnLabel(col.key)}
                                    {col.required
                                      ? ` * (${t('NLSOpsCsvRequiredBadge')})`
                                      : ''}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {serverStep === 'dryRun' && dryRunResult && (
                <div className="bulkImportServerDryRun">
                  <div className="bulkImportPreviewSummary">
                    <div className="bulkImportPreviewBadges">
                      <span className="bulkImportBadge bulkImportBadge--total">
                        {t('NLSOpsCsvDryRunSummaryTotal', {
                          count: dryRunResult.totalRows,
                        })}
                      </span>
                      <span className="bulkImportBadge bulkImportBadge--valid">
                        {t('NLSOpsCsvDryRunSummaryValid', {
                          count: dryRunResult.validRows,
                        })}
                      </span>
                      <span className="bulkImportBadge bulkImportBadge--create">
                        {t('NLSOpsCsvDryRunSummaryCreate', {
                          count: dryRunResult.counts.create,
                        })}
                      </span>
                      <span className="bulkImportBadge bulkImportBadge--update">
                        {t('NLSOpsCsvDryRunSummaryUpdate', {
                          count: dryRunResult.counts.update,
                        })}
                      </span>
                      <span className="bulkImportBadge bulkImportBadge--unchanged">
                        {t('NLSOpsCsvDryRunSummaryUnchanged', {
                          count: dryRunResult.counts.unchanged,
                        })}
                      </span>
                      {dryRunFatalErrors.length > 0 && (
                        <span className="bulkImportBadge bulkImportBadge--error">
                          {t('NLSOpsCsvDryRunSummaryErrors', {
                            count: dryRunFatalErrors.length,
                          })}
                        </span>
                      )}
                      {dryRunWarnings.length > 0 && (
                        <span className="bulkImportBadge bulkImportBadge--warning">
                          {t('NLSOpsCsvDryRunSummaryWarnings', {
                            count: dryRunWarnings.length,
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  {actionError && (
                    <div className="bulkImportBanner bulkImportBanner--error" data-testid="apply-error-banner">
                      <ErrorOutlineRoundedIcon fontSize="small" />
                      <span>{actionError}</span>
                    </div>
                  )}

                  {/* Fatal errors table */}
                  {dryRunFatalErrors.length > 0 && (
                    <div className="bulkImportSection" data-testid="fatal-errors-section">
                      <div className="bulkImportSection__head">
                        <h4>
                          {t('NLSOpsCsvRowErrorsTableTitle', {
                            count: dryRunFatalErrors.length,
                          })}
                        </h4>
                        <button
                          type="button"
                          className="bulkImportLinkBtn"
                          onClick={handleDownloadErrors}
                          data-testid="download-errors-btn"
                        >
                          {t('NLSOpsCsvActionDownloadErrors')}
                        </button>
                      </div>
                      <div className="bulkImportGridWrapper">
                        <table className="bulkImportGrid">
                          <thead>
                            <tr>
                              <th className="bulkImportTh--width60">{t('NLSOpsCsvColRowNumber')}</th>
                              <th>{t('NLSOpsCsvColField')}</th>
                              <th>{t('NLSOpsCsvColError')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dryRunFatalErrors.map((err, i) => (
                              <tr key={i} className="hasError">
                                <td className="bulkImportTd--muted">{err.rowNumber}</td>
                                <td>{server.columnLabel(err.field)}</td>
                                <td>{server.formatRowError(err.code)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Warnings table */}
                  {dryRunWarnings.length > 0 && (
                    <div className="bulkImportSection" data-testid="warnings-section">
                      <div className="bulkImportSection__head">
                        <h4>
                          {t('NLSOpsCsvRowWarningsTableTitle', {
                            count: dryRunWarnings.length,
                          })}
                        </h4>
                      </div>
                      <div className="bulkImportGridWrapper">
                        <table className="bulkImportGrid">
                          <thead>
                            <tr>
                              <th className="bulkImportTh--width60">{t('NLSOpsCsvColRowNumber')}</th>
                              <th>{t('NLSOpsCsvColField')}</th>
                              <th>{t('NLSOpsCsvColWarning')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dryRunWarnings.map((w, i) => (
                              <tr key={i}>
                                <td className="bulkImportTd--muted">{w.rowNumber}</td>
                                <td>{server.columnLabel(w.field)}</td>
                                <td>{server.formatRowError(w.code)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Preview table */}
                  {dryRunResult.preview.length > 0 && (
                    <div className="bulkImportSection" data-testid="preview-section">
                      <div className="bulkImportSection__head">
                        <h4>
                          {t('NLSOpsCsvPreviewTableTitle', {
                            count: dryRunResult.preview.length,
                          })}
                        </h4>
                      </div>
                      <div className="bulkImportGridWrapper">
                        <table className="bulkImportGrid">
                          <thead>
                            <tr>
                              <th className="bulkImportTh--width40">#</th>
                              {Object.keys(dryRunResult.preview[0] || {}).map((k) => (
                                <th key={k}>{k}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {dryRunResult.preview.map((row, idx) => (
                              <tr key={idx}>
                                <td className="bulkImportTd--muted">{idx + 1}</td>
                                {Object.keys(dryRunResult.preview[0] || {}).map((k) => (
                                  <td key={k}>{row[k] || <span className="bulkImportSpan--dash">—</span>}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {serverStep === 'apply' && (
                <div className="bulkImportProgressCard">
                  {isServerDone ? (
                    <>
                      <CheckCircleRoundedIcon sx={{ fontSize: 48, color: 'var(--success)' }} />
                      <h3>{t('NLSOpsCsvApplyComplete')}</h3>
                      <p className="bulkImportP--muted">
                        {t('NLSOpsCsvApplyCompleteDesc', {
                          total: applyJobDoc?.totalRows ?? dryRunResult?.totalRows ?? 0,
                          created: applyJobDoc?.counts?.create ?? dryRunResult?.counts?.create ?? 0,
                          updated: applyJobDoc?.counts?.update ?? dryRunResult?.counts?.update ?? 0,
                        })}
                      </p>
                    </>
                  ) : isServerPartial ? (
                    <>
                      <WarningAmberRoundedIcon sx={{ fontSize: 48, color: 'var(--warning)' }} />
                      <h3>{t('NLSOpsCsvApplyPartial')}</h3>
                      <p className="bulkImportP--muted">
                        {t('NLSOpsCsvApplyPartialDesc')}
                      </p>
                      {applyJobDoc?.rowResults && (
                        <div className="bulkImportGridWrapper bulkImportGridWrapper--fullMargin">
                          <table className="bulkImportGrid">
                            <thead>
                              <tr>
                                <th>{t('NLSOpsCsvColRowNumber')}</th>
                                <th>{t('NLSOpsCsvColError')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {applyJobDoc.rowResults
                                .filter((r) => r.status === 'ERROR')
                                .map((r, i) => (
                                  <tr key={i} className="hasError">
                                    <td>{r.rowNumber}</td>
                                    <td>{r.error || r.code || 'Refused'}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  ) : actionError ? (
                    <>
                      <ErrorOutlineRoundedIcon sx={{ fontSize: 48, color: 'var(--danger)' }} />
                      <h3>{t('NLSOpsCsvApplyFailed')}</h3>
                      <p className="bulkImportP--danger">{actionError}</p>
                    </>
                  ) : (
                    <>
                      <h3>
                        {t('NLSOpsCsvApplyingProgress', {
                          processed: applyJobDoc?.rowResults?.length ?? applyJobDoc?.validRows ?? 0,
                          total: applyJobDoc?.totalRows ?? dryRunResult?.totalRows ?? 0,
                        })}
                      </h3>
                      <div className="bulkImportProgressBar">
                        <div
                          className="bulkImportProgressBarFill"
                          ref={(el) => {
                            if (el) {
                              const pct = applyJobDoc?.totalRows
                                ? Math.min(
                                    100,
                                    ((applyJobDoc.rowResults?.length ?? applyJobDoc.validRows ?? 0) /
                                      applyJobDoc.totalRows) *
                                      100,
                                  )
                                : isSubmitting
                                  ? 50
                                  : 0
                              el.style.width = `${pct}%`
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppDialog>
  )
}
